import * as React from "react";
import { Alert, Snackbar } from "@mui/material";
import { ExtendedAccount } from "@/types/ExtendedAccount";
import { Account, AccountNextStep, Person, ServiceProvider, ServiceProviderType } from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { TextInput } from "@/components/atoms/TextInput";
import { FormField } from "@/components/molecules/FormField";
import { useOnChange } from "@/utils/callbacks";

import { AccountFieldProps } from "./types/AccountFieldProps";
import { NestedErrors } from "@/utils/Types";

export type SocialMediaOrEmailAccount = {
  readonly emailAddress?: string;
  readonly phoneNumber?: string;
  readonly profileUrl?: string;
};

export type SocialMediaOrEmailAccountErrors = NestedErrors<SocialMediaOrEmailAccount> | undefined;

const accountRequest = AccountNextStep.Cancel;

export const errorsOf: (r: SocialMediaOrEmailAccount) => SocialMediaOrEmailAccountErrors = (
  record
) => {
  return cleanedErrors({
    emailAddress: record?.emailAddress ? undefined : "required",
    // phoneNumber: record?.phoneNumber ? undefined : "required",
  });
};

export const recordFromAccount = (account?: Account): SocialMediaOrEmailAccount => {
  if (!account) {
    return {};
  }

  return {
    emailAddress: account.emailAddress,
    phoneNumber: account.phoneNumber,
    profileUrl: account.profileUrl,
  };
};

export type SocialMediaOrEmailAccountFieldsProps = AccountFieldProps;

export const SocialMediaOrEmailAccountFields: React.FC<SocialMediaOrEmailAccountFieldsProps> = ({
  serviceProvider,
  account,
  setBusy,
  onAccountAdded,
  updateTemplate,

  saveAccount,
  saveRecord,
  savedRecord,
}) => {
  const [record, update] = React.useState(savedRecord || recordFromAccount(account));
  const [errors, setErrors] = React.useState({} as SocialMediaOrEmailAccountErrors);
  const [remoteErrors, setRemoteErrors] = React.useState(undefined as string | undefined);

  const onChange = useOnChange(update);

  React.useEffect(() => {
    if (!updateTemplate) {
      return;
    }

    updateTemplate({
      onNext: !serviceProvider?.serviceProviderType
        ? undefined
        : () => {
            const errors = errorsOf(record);

            if (errors) {
              setErrors(errors);
              if (saveRecord) {
                saveRecord({ source: record });
              }
              return;
            }

            const data = {
              id: account?.id,
              serviceProvider: {
                id: serviceProvider.serviceProviderId,
                companyName: serviceProvider.customProviderName,
                serviceProviderType: serviceProvider.serviceProviderType,
              },

              emailAddress: record.emailAddress,
              phoneNumber: record.phoneNumber,
              profileUrl: record.profileUrl,
              nextStep: accountRequest,
            };

            if (saveRecord) {
              saveRecord({ source: record, target: data });
              return { response: {} };
            }

            setBusy(true);

            return saveAccount({
              sector: ServiceProviderType.SocialMedia,
              record: data,
            }).then(
              (
                response: {
                  data: {
                    account: ExtendedAccount;
                    newServiceProvider?: ServiceProvider;
                    newPerson?: Person;
                  };
                } & { error: Error }
              ) => {
                setBusy(false);
                if (response.error) {
                  setRemoteErrors(response.error.message);
                  return;
                }
                if (response.data) {
                  onAccountAdded(response.data.account, {
                    newServiceProvider: response.data.newServiceProvider,
                    newPerson: response.data.newPerson,
                  });
                  return;
                }
              },
              (err: Error) => {
                console.warn({ err });
                setBusy(false);
                setRemoteErrors("Operation failed. Please try again or contact customer support.");
              }
            );
          },
    });
  }, [updateTemplate, serviceProvider, account, onAccountAdded, record, setBusy, saveAccount]);

  return (
    <>
      <FormField halfWidth label="Associated email address">
        {" "}
        <TextInput
          name={"emailAddress"}
          value={record.emailAddress || ""}
          error={errors?.emailAddress}
          onChange={onChange}
        />{" "}
      </FormField>

      {serviceProvider.serviceProviderType !== ServiceProviderType.Email && (
        <>
          <FormField halfWidth label="Associated phone number">
            {" "}
            <TextInput
              name={"phoneNumber"}
              value={record.phoneNumber || ""}
              error={errors?.phoneNumber}
              onChange={onChange}
            />{" "}
          </FormField>

          <FormField twoThirdsWidthByItself label="Associated profile link (if available)">
            {" "}
            <TextInput
              name={"profileUrl"}
              value={record.profileUrl || ""}
              error={errors?.profileUrl}
              onChange={onChange}
            />{" "}
          </FormField>
        </>
      )}

      <FormField>We will assist you in closing this account.</FormField>

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteErrors}
        autoHideDuration={6000}
        onClose={() => setRemoteErrors(undefined)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={() => setRemoteErrors(undefined)}
        >
          {remoteErrors}
        </Alert>
      </Snackbar>
    </>
  );
};
